import React, { useMemo } from "react";
import styled from "styled-components/macro";
import {
  Typography,
  Divider,
  Chip,
  Tabs,
  Tab,
  Box,
  Link,
} from "@material-ui/core";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import ListAltIcon from "@material-ui/icons/ListAlt";
import EditIcon from "@material-ui/icons/Edit";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import {
  fmtDate,
  fmtNum,
  fmtPaValue,
  fmtResult,
  isPaParameter,
} from "./format";
import { FieldServicesAppRoutes } from "../../fieldServicesApp/lib/constants";

const Wrap = styled.div`
  width: 360px;
  max-height: 360px;
  display: flex;
  flex-direction: column;
`;

const Body = styled.div`
  overflow-y: auto;
  padding-top: 8px;
`;

const StatTable = styled.table`
  border-collapse: collapse;
  width: 100%;

  td {
    padding: 3px 6px;
    vertical-align: top;
    font-size: 12px;
  }

  tr:nth-of-type(even) {
    background-color: #f5f5f5;
  }
`;

const Label = styled.td`
  color: #555;
  font-weight: 600;
  white-space: nowrap;
  width: 40%;
`;

const Notes = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #444;
  font-style: italic;
  white-space: pre-wrap;
`;

const ParameterPanel = ({
  stat,
  wellNdx,
  onShowRecords,
  onEditEvent,
  latestEditableEventByWellParam,
}) => {
  if (!stat) return null;
  const canShowRecords =
    typeof onShowRecords === "function" &&
    wellNdx != null &&
    stat.aqsci_parameter_ndx != null &&
    stat.result_count > 0;
  const latestEditable =
    typeof onEditEvent === "function" && latestEditableEventByWellParam
      ? latestEditableEventByWellParam.get(
          `${wellNdx}-${stat.aqsci_parameter_ndx}`
        )
      : null;
  const isPa = isPaParameter(stat.aqsci_parameter_ndx);
  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        style={{ gap: 6 }}
      >
        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
          <Typography variant="subtitle2">
            {stat.parameter_name || stat.parameter_abbrev}
          </Typography>
          {stat.is_nondetect && !isPa && (
            <Chip
              size="small"
              label="ND"
              style={{ height: 18, fontSize: 10 }}
            />
          )}
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 10 }}>
          {latestEditable && (
            <Link
              component="button"
              onClick={() => onEditEvent(latestEditable)}
              underline="hover"
              variant="caption"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 2,
                whiteSpace: "nowrap",
              }}
              title="Edit the most recent sampling event for this parameter"
            >
              <EditIcon style={{ fontSize: 14 }} />
              Edit latest
            </Link>
          )}
          {canShowRecords && (
            <Link
              component="button"
              onClick={() => onShowRecords(wellNdx, stat.aqsci_parameter_ndx)}
              underline="hover"
              variant="caption"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 2,
                whiteSpace: "nowrap",
              }}
              title="See every record for this well and parameter"
            >
              <ListAltIcon style={{ fontSize: 14 }} />
              View {stat.result_count} record
              {stat.result_count === 1 ? "" : "s"}
            </Link>
          )}
        </Box>
      </Box>
      <StatTable>
        <tbody>
          <tr>
            <Label>Last Result</Label>
            <td>
              {isPa
                ? fmtPaValue(stat.last_report)
                : `${fmtResult(stat.last_report_entered ?? stat.last_report)} ${
                    stat.unit_abbrev || ""
                  }`}
            </td>
          </tr>
          <tr>
            <Label>First / Last Date</Label>
            <td>
              {fmtDate(stat.first_result)} – {fmtDate(stat.last_result)}
            </td>
          </tr>
          {!isPa && (
            <tr>
              <Label>Min / Max / Avg</Label>
              <td>
                {fmtNum(stat.min_result)} / {fmtNum(stat.max_result)} /{" "}
                {fmtNum(stat.avg_result)}
              </td>
            </tr>
          )}
          {stat.equipment ? (
            <tr>
              <Label>Equipment</Label>
              <td>{stat.equipment}</td>
            </tr>
          ) : null}
          {stat.equip_lab_other ? (
            <tr>
              <Label>Lab / Other</Label>
              <td>{stat.equip_lab_other}</td>
            </tr>
          ) : null}
          {stat.sampled_by ? (
            <tr>
              <Label>Sampled By</Label>
              <td>{stat.sampled_by}</td>
            </tr>
          ) : null}
        </tbody>
      </StatTable>
      {stat.notes && <Notes>“{stat.notes}”</Notes>}
    </Box>
  );
};

const WqMapPopup = ({
  summary,
  paramStats,
  activeParameterNdx,
  onTabChange,
  onShowRecords,
  onEditEvent,
  latestEditableEventByWellParam,
}) => {
  // paramStats: array of stat rows for THIS well, sorted alphabetically by abbrev

  const effectiveActiveNdx = useMemo(() => {
    if (!paramStats?.length) return null;
    const hasActive =
      activeParameterNdx != null &&
      paramStats.some((s) => s.aqsci_parameter_ndx === activeParameterNdx);
    return hasActive ? activeParameterNdx : paramStats[0].aqsci_parameter_ndx;
  }, [paramStats, activeParameterNdx]);

  if (!summary) return null;

  const activeStat = paramStats?.find(
    (s) => s.aqsci_parameter_ndx === effectiveActiveNdx
  );

  const lat = Number(summary.lat_dd);
  const lon = Number(summary.lon_dd);
  const coords =
    Number.isFinite(lat) && Number.isFinite(lon)
      ? `${lat.toFixed(5)}, ${lon.toFixed(5)}`
      : null;

  const wellEditHref = summary.well_ndx
    ? `/data-management/wells/wells-management/${summary.well_ndx}`
    : null;
  const addRecordHref = summary.district_well_id
    ? FieldServicesAppRoutes.WATER_QUALITY(summary.district_well_id)
    : null;

  return (
    <Wrap>
      {wellEditHref ? (
        <Link
          href={wellEditHref}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          variant="subtitle1"
          style={{
            fontWeight: 700,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
          title="Open well record in a new tab"
        >
          {summary.well_name}
          <OpenInNewIcon style={{ fontSize: 14 }} />
        </Link>
      ) : (
        <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
          {summary.well_name}
        </Typography>
      )}
      <Typography variant="caption" color="textSecondary" display="block">
        {summary.samplecount} samples · {fmtDate(summary.first_sample)} to{" "}
        {fmtDate(summary.last_sample)}
      </Typography>
      {coords && (
        <Typography variant="caption" color="textSecondary" display="block">
          {coords}
        </Typography>
      )}
      {addRecordHref && (
        <Link
          href={addRecordHref}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          variant="caption"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 2,
            marginTop: 4,
            whiteSpace: "nowrap",
          }}
          title="Open the field services WQ entry form for this well in a new tab"
        >
          <AddCircleOutlineIcon style={{ fontSize: 14 }} />
          Add a new record
        </Link>
      )}

      {paramStats?.length > 0 && (
        <>
          <Divider style={{ margin: "8px 0 0" }} />
          <Tabs
            value={effectiveActiveNdx}
            onChange={(_, ndx) => onTabChange?.(ndx)}
            variant="scrollable"
            scrollButtons="auto"
            indicatorColor="primary"
            textColor="primary"
            style={{ minHeight: 36 }}
          >
            {paramStats.map((s) => (
              <Tab
                key={s.aqsci_parameter_ndx}
                value={s.aqsci_parameter_ndx}
                label={s.parameter_abbrev || s.parameter_name}
                style={{ minHeight: 36, minWidth: 60, padding: "6px 12px" }}
              />
            ))}
          </Tabs>
          <Body>
            <ParameterPanel
              stat={activeStat}
              wellNdx={summary.well_ndx}
              onShowRecords={onShowRecords}
              onEditEvent={onEditEvent}
              latestEditableEventByWellParam={latestEditableEventByWellParam}
            />
          </Body>
        </>
      )}
    </Wrap>
  );
};

export default WqMapPopup;
